import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';

import { FILTER_COLOR } from 'dpl/constants/query_params';
import useSearchQueryFilter from 'dpl/breeder_search_results/hooks/useSearchQueryFilter';
import { capitalize, isMobileUA } from 'dpl/shared/utils';

import ExpandableChecklist from './ExpandableChecklist';
import { COLOR_TYPES } from '../utils/constants';

const IS_MOBILE = isMobileUA();
const MAX_FOR_GRID = 8;

function ColorFilterSection({
  checklistName,
  className,
  isColorFilterButton,
  onChange,
  opts,
  seeMoreAfter,
  title,
  values
}) {
  return (
    <div
      className={classnames('flex flex-column', {
        'gap-16': isColorFilterButton,
        'gap-24': !isColorFilterButton
      })}
    >
      {title && <p className="font-16 fw-medium">{title}</p>}
      <ExpandableChecklist
        className={className}
        checklistName={checklistName}
        options={opts}
        seeMoreAfter={seeMoreAfter}
        values={values}
        onChange={onChange}
      />
    </div>
  );
}

ColorFilterSection.propTypes = {
  className: PropTypes.string,
  checklistName: PropTypes.string.isRequired,
  isColorFilterButton: PropTypes.bool.isRequired,
  onChange: PropTypes.func.isRequired,
  opts: PropTypes.arrayOf(
    PropTypes.oneOfType([
      PropTypes.shape({
        title: PropTypes.string.isRequired,
        value: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
          .isRequired
      }),
      PropTypes.shape({ label: PropTypes.string }).isRequired
    ])
  ).isRequired,
  seeMoreAfter: PropTypes.number.isRequired,
  title: PropTypes.string,
  values: PropTypes.arrayOf(
    PropTypes.oneOfType([PropTypes.string, PropTypes.number])
  ).isRequired
};

ColorFilterSection.defaultProps = {
  className: '',
  title: null
};

export default function ColorFilter({
  title,
  filterOptions,
  isColorFilterButton
}) {
  const { searchQueryFilterValues, toggleFilter } =
    useSearchQueryFilter(FILTER_COLOR);

  const nonStandardColors = useMemo(
    () =>
      (filterOptions || []).find(
        opt => opt[0] === COLOR_TYPES.NON_STANDARD
      )?.[1] || [],
    [filterOptions]
  );
  const standardColors = useMemo(
    () =>
      (filterOptions || []).find(opt => opt[0] === COLOR_TYPES.STANDARD)?.[1] ||
      [],
    [filterOptions]
  );

  const hasStandardAndNonStandardColors =
    standardColors.length > 0 && nonStandardColors.length > 0;

  function getSeeMoreAfterNum() {
    if (isColorFilterButton) return 0;
    if (IS_MOBILE || hasStandardAndNonStandardColors) return 6;

    return 12;
  }

  const checklistCommonProps = {
    checklistName: FILTER_COLOR,
    values: searchQueryFilterValues,
    onChange: e => toggleFilter(Number(e.target.value)),
    seeMoreAfter: getSeeMoreAfterNum(),
    isColorFilterButton
  };

  const isOverflowContainer =
    isColorFilterButton &&
    !IS_MOBILE &&
    (hasStandardAndNonStandardColors ||
      standardColors.length > MAX_FOR_GRID ||
      nonStandardColors.length > MAX_FOR_GRID);

  return (
    <>
      {title && <h4 className="font-20 lh-title mb6">{title}</h4>}
      <div
        className={classnames('SearchResultFiltersColorFilter', {
          'overflow-y-scroll ph1 nh1 pt1 nt1': isOverflowContainer,
          pb4: isColorFilterButton && !IS_MOBILE
        })}
        style={
          isOverflowContainer ? { height: '180px', width: '540px' } : undefined
        }
      >
        {standardColors.length > 0 && nonStandardColors.length > 0 && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-24">
            <ColorFilterSection
              title={COLOR_TYPES.STANDARD}
              opts={standardColors}
              {...checklistCommonProps}
            />
            <ColorFilterSection
              title={capitalize(COLOR_TYPES.NON_STANDARD.toLowerCase())}
              opts={nonStandardColors}
              {...checklistCommonProps}
            />
          </div>
        )}

        {/* only standard colors */}
        {standardColors.length > 0 && nonStandardColors.length === 0 && (
          <ColorFilterSection
            className={classnames({
              'md:grid-cols-2 gap-x-24': standardColors.length > MAX_FOR_GRID
            })}
            opts={standardColors}
            {...checklistCommonProps}
          />
        )}

        {/* only nonstandard colors */}
        {nonStandardColors.length > 0 && standardColors.length === 0 && (
          <ColorFilterSection
            className={classnames({
              'md:grid-cols-2 gap-x-24': nonStandardColors.length > MAX_FOR_GRID
            })}
            opts={nonStandardColors}
            {...checklistCommonProps}
          />
        )}
      </div>
    </>
  );
}

ColorFilter.propTypes = {
  title: PropTypes.string,
  filterOptions: PropTypes.arrayOf(PropTypes.array).isRequired,
  isColorFilterButton: PropTypes.bool
};

ColorFilter.defaultProps = {
  title: null,
  isColorFilterButton: false
};
